import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { 
  BookOpen, Sparkles, Calendar, Clock, RefreshCw, Save, 
  Plus, Edit2, Trash2, GripVertical, Search, Filter,
  CheckCircle, AlertCircle, Library, FileText, Star,
  CheckCircle2, Circle, MessageSquare, ClipboardList, BarChart3, Download
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const DOMAIN_COLORS = {
  cognitive: 'bg-purple-100 text-purple-800',
  language: 'bg-blue-100 text-blue-800',
  literacy: 'bg-indigo-100 text-indigo-800',
  math: 'bg-green-100 text-green-800',
  science: 'bg-teal-100 text-teal-800',
  social_emotional: 'bg-pink-100 text-pink-800',
  fine_motor: 'bg-orange-100 text-orange-800',
  gross_motor: 'bg-red-100 text-red-800',
  creative: 'bg-yellow-100 text-yellow-800',
  music: 'bg-violet-100 text-violet-800',
  sensory: 'bg-cyan-100 text-cyan-800',
  self_help: 'bg-emerald-100 text-emerald-800'
};

const DOMAIN_LABELS = {
  cognitive: 'Cognitive',
  language: 'Language',
  literacy: 'Literacy',
  math: 'Math',
  science: 'Science',
  social_emotional: 'Social-Emotional',
  fine_motor: 'Fine Motor',
  gross_motor: 'Gross Motor',
  creative: 'Creative',
  music: 'Music',
  sensory: 'Sensory',
  self_help: 'Self-Help'
};

const AGE_GROUPS = [
  { value: 'infant', label: 'Infant (0-12 months)' },
  { value: 'toddler', label: 'Toddler (12-36 months)' },
  { value: 'preschool', label: 'Preschool (3-5 years)' },
  { value: 'school_age', label: 'School Age (5+ years)' }
];

const LearningPage = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('plans');
  const [loading, setLoading] = useState(false);
  
  // Activities state
  const [activities, setActivities] = useState([]);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityFilters, setActivityFilters] = useState({ age_group: '', domain: '' });
  
  // Lesson plans state
  const [lessonPlans, setLessonPlans] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [domains, setDomains] = useState([]);
  const [themes, setThemes] = useState([]);
  
  // Observations history state
  const [observationHistory, setObservationHistory] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({ classroom_id: '', domain: '' });
  const [childProgress, setChildProgress] = useState(null);
  
  // Generate dialog state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [useAiGeneration, setUseAiGeneration] = useState(true);
  const [generateForm, setGenerateForm] = useState({
    classroom_id: '',
    age_group: '',
    week_start: new Date().toISOString().split('T')[0],
    theme: '',
    include_outdoor: true,
    special_focus: ''
  });
  
  // Preview state
  const [previewPlan, setPreviewPlan] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [planProgress, setPlanProgress] = useState(null);
  const [isPlanSaved, setIsPlanSaved] = useState(false);
  
  // Custom plan dialog state
  const [showCustomPlanDialog, setShowCustomPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [customPlanForm, setCustomPlanForm] = useState({
    title: '',
    classroom_id: '',
    age_group: '',
    week_start: new Date().toISOString().split('T')[0],
    theme: '',
    days: []
  });
  
  // Edit activity in plan dialog
  const [showEditActivityDialog, setShowEditActivityDialog] = useState(false);
  const [editActivityTarget, setEditActivityTarget] = useState(null);
  const [editActivityForm, setEditActivityForm] = useState({
    activity_name: '',
    domain: '',
    time_slot: '',
    duration_minutes: 30,
    materials: '',
    instructions: ''
  });
  
  // Observation dialog state
  const [showObservationDialog, setShowObservationDialog] = useState(false);
  const [observationTarget, setObservationTarget] = useState(null);
  const [observationForm, setObservationForm] = useState({
    notes: '',
    observation: '',
    child_responses: ''
  });
  
  // Activity dialog state
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [activityForm, setActivityForm] = useState({
    name: '',
    description: '',
    age_groups: [],
    domains: [],
    duration_minutes: 15,
    materials: '',
    learning_objectives: '',
    location: 'indoor',
    energy_level: 'medium',
    instructions: ''
  });

  useEffect(() => {
    fetchClassrooms();
    fetchDomains();
    fetchThemes();
  }, []);

  useEffect(() => {
    if (activeTab === 'activities') {
      fetchActivities();
    } else if (activeTab === 'plans') {
      fetchLessonPlans();
    } else if (activeTab === 'observations') {
      fetchObservationHistory();
    }
  }, [activeTab, activityFilters, historyFilters]);

  const fetchClassrooms = async () => {
    try {
      const res = await axios.get(`${API}/classrooms`, getAuthHeaders());
      setClassrooms(res.data);
    } catch (error) {
      console.error('Failed to fetch classrooms', error);
    }
  };

  const fetchDomains = async () => {
    try {
      const res = await axios.get(`${API}/learning/domains`, getAuthHeaders());
      setDomains(res.data);
    } catch (error) {
      console.error('Failed to fetch domains', error);
    }
  };

  const fetchThemes = async () => {
    try {
      const res = await axios.get(`${API}/learning/themes`, getAuthHeaders());
      setThemes(res.data);
    } catch (error) {
      console.error('Failed to fetch themes', error);
    }
  };

  const fetchObservationHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (historyFilters.classroom_id) params.append('classroom_id', historyFilters.classroom_id);
      if (historyFilters.domain) params.append('domain', historyFilters.domain);
      
      const res = await axios.get(`${API}/learning/observations/history?${params}`, getAuthHeaders());
      setObservationHistory(res.data);
      
      // Also fetch child progress if classroom selected
      if (historyFilters.classroom_id) {
        const progressRes = await axios.get(
          `${API}/learning/observations/child-progress?classroom_id=${historyFilters.classroom_id}&weeks=8`,
          getAuthHeaders()
        );
        setChildProgress(progressRes.data);
      } else {
        setChildProgress(null);
      }
    } catch (error) {
      toast.error('Failed to load observation history');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (activityFilters.age_group) params.append('age_group', activityFilters.age_group);
      if (activityFilters.domain) params.append('domain', activityFilters.domain);
      if (activitySearch) params.append('search', activitySearch);
      
      const res = await axios.get(`${API}/learning/activities?${params}`, getAuthHeaders());
      setActivities(res.data);
    } catch (error) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonPlans = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/learning/lesson-plans`, getAuthHeaders());
      setLessonPlans(res.data);
    } catch (error) {
      toast.error('Failed to load lesson plans');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePlan = async () => {
    if (!generateForm.classroom_id || !generateForm.age_group || !generateForm.week_start) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Choose endpoint based on AI toggle
      const endpoint = useAiGeneration 
        ? `${API}/learning/lesson-plans/generate-ai`
        : `${API}/learning/lesson-plans/generate`;
      
      const res = await axios.post(endpoint, generateForm, getAuthHeaders());
      setPreviewPlan(res.data);
      setPlanProgress(null);
      setIsPlanSaved(false);
      setShowGenerateDialog(false);
      setShowPreviewDialog(true);
      toast.success(useAiGeneration ? 'AI lesson plan generated!' : 'Lesson plan generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate plan');
    } finally {
      setLoading(false);
    }
  };

  const handleRegeneratePlan = async () => {
    // Use existing plan data for regeneration
    const params = {
      classroom_id: previewPlan?.classroom_id || generateForm.classroom_id,
      age_group: previewPlan?.age_group || generateForm.age_group,
      week_start: previewPlan?.week_start || generateForm.week_start,
      theme: previewPlan?.theme || generateForm.theme,
      include_outdoor: generateForm.include_outdoor !== false,
      special_focus: generateForm.special_focus || ''
    };

    if (!params.classroom_id || !params.age_group || !params.week_start) {
      toast.error('Missing plan parameters');
      return;
    }

    try {
      setLoading(true);
      
      // Use same generation mode as original
      const endpoint = previewPlan?.generated_by_ai
        ? `${API}/learning/lesson-plans/generate-ai`
        : `${API}/learning/lesson-plans/generate`;
      
      const res = await axios.post(endpoint, params, getAuthHeaders());
      setPreviewPlan(res.data);
      setPlanProgress(null);
      setIsPlanSaved(false);
      toast.success('Lesson plan regenerated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate plan');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async () => {
    if (!previewPlan) return;

    try {
      setLoading(true);
      const res = await axios.post(`${API}/learning/lesson-plans`, previewPlan, getAuthHeaders());
      toast.success('Lesson plan saved! You can now export it as PDF.');
      setIsPlanSaved(true);
      fetchLessonPlans();
    } catch (error) {
      toast.error('Failed to save plan');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanProgress = async (planId) => {
    try {
      const res = await axios.get(`${API}/learning/lesson-plans/${planId}/progress`, getAuthHeaders());
      setPlanProgress(res.data);
    } catch (error) {
      console.error('Failed to fetch progress', error);
    }
  };

  const handleToggleComplete = async (dayIndex, activityIndex, currentStatus) => {
    if (!previewPlan?.id) {
      toast.error('Please save the plan first to track completion');
      return;
    }

    try {
      await axios.put(
        `${API}/learning/lesson-plans/${previewPlan.id}/activities/${dayIndex}/${activityIndex}`,
        { completed: !currentStatus },
        getAuthHeaders()
      );
      
      // Update local state
      const updatedPlan = { ...previewPlan };
      updatedPlan.days[dayIndex].activities[activityIndex].completed = !currentStatus;
      if (!currentStatus) {
        updatedPlan.days[dayIndex].activities[activityIndex].completed_at = new Date().toISOString();
      }
      setPreviewPlan(updatedPlan);
      
      // Refresh progress
      fetchPlanProgress(previewPlan.id);
      
      toast.success(!currentStatus ? 'Activity marked as complete!' : 'Activity marked as incomplete');
    } catch (error) {
      toast.error('Failed to update activity');
    }
  };

  const openObservationDialog = (dayIndex, activityIndex, activity) => {
    setObservationTarget({ dayIndex, activityIndex, activity });
    setObservationForm({
      notes: activity.notes || '',
      observation: activity.observation || '',
      child_responses: activity.child_responses || ''
    });
    setShowObservationDialog(true);
  };

  const handleSaveObservation = async () => {
    if (!previewPlan?.id || !observationTarget) {
      toast.error('Please save the plan first');
      return;
    }

    try {
      setLoading(true);
      await axios.put(
        `${API}/learning/lesson-plans/${previewPlan.id}/activities/${observationTarget.dayIndex}/${observationTarget.activityIndex}`,
        observationForm,
        getAuthHeaders()
      );
      
      // Update local state
      const updatedPlan = { ...previewPlan };
      const activity = updatedPlan.days[observationTarget.dayIndex].activities[observationTarget.activityIndex];
      activity.notes = observationForm.notes;
      activity.observation = observationForm.observation;
      activity.child_responses = observationForm.child_responses;
      setPreviewPlan(updatedPlan);
      
      setShowObservationDialog(false);
      toast.success('Observation saved!');
      
      // Refresh progress
      fetchPlanProgress(previewPlan.id);
    } catch (error) {
      toast.error('Failed to save observation');
    } finally {
      setLoading(false);
    }
  };

  const handleExportProgressPDF = async () => {
    if (!historyFilters.classroom_id) {
      toast.error('Please select a classroom first');
      return;
    }

    try {
      setLoading(true);
      const authHeaders = getAuthHeaders();
      const response = await axios.get(
        `${API}/learning/observations/export-pdf?classroom_id=${historyFilters.classroom_id}&weeks=8`,
        {
          headers: authHeaders.headers,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `progress_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleExportLessonPlanPDF = async () => {
    if (!previewPlan?.id || !isPlanSaved) {
      toast.error('Please save the plan first to export');
      return;
    }

    try {
      setLoading(true);
      const authHeaders = getAuthHeaders();
      const response = await axios({
        method: 'GET',
        url: `${API}/learning/lesson-plans/${previewPlan.id}/export-pdf`,
        headers: authHeaders.headers,
        responseType: 'blob'
      });
      
      // Verify we received a blob
      if (!(response.data instanceof Blob)) {
        console.error('Response is not a Blob:', response.data);
        toast.error('Invalid PDF response');
        return;
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lesson_plan_${previewPlan.week_start || 'plan'}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Lesson plan PDF downloaded!');
    } catch (error) {
      console.error('PDF export error:', error);
      console.error('Error response:', error.response);
      toast.error('Failed to export PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveActivity = async () => {
    if (!activityForm.name || !activityForm.description || activityForm.age_groups.length === 0 || activityForm.domains.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const data = {
        ...activityForm,
        materials: activityForm.materials.split(',').map(m => m.trim()).filter(Boolean),
        learning_objectives: activityForm.learning_objectives.split(',').map(l => l.trim()).filter(Boolean)
      };

      if (editingActivity) {
        await axios.put(`${API}/learning/activities/${editingActivity.id}`, data, getAuthHeaders());
        toast.success('Activity updated!');
      } else {
        await axios.post(`${API}/learning/activities`, data, getAuthHeaders());
        toast.success('Activity created!');
      }
      
      setShowActivityDialog(false);
      resetActivityForm();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save activity');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteActivity = async (id) => {
    if (!confirm('Delete this activity?')) return;
    
    try {
      await axios.delete(`${API}/learning/activities/${id}`, getAuthHeaders());
      toast.success('Activity deleted');
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete activity');
    }
  };

  const handleDeletePlan = async (id) => {
    if (!confirm('Delete this lesson plan?')) return;
    
    try {
      await axios.delete(`${API}/learning/lesson-plans/${id}`, getAuthHeaders());
      toast.success('Lesson plan deleted');
      fetchLessonPlans();
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const resetActivityForm = () => {
    setEditingActivity(null);
    setActivityForm({
      name: '',
      description: '',
      age_groups: [],
      domains: [],
      duration_minutes: 15,
      materials: '',
      learning_objectives: '',
      location: 'indoor',
      energy_level: 'medium',
      instructions: ''
    });
  };

  const openEditActivity = (activity) => {
    setEditingActivity(activity);
    setActivityForm({
      name: activity.name,
      description: activity.description,
      age_groups: activity.age_groups || [],
      domains: activity.domains || [],
      duration_minutes: activity.duration_minutes || 15,
      materials: (activity.materials || []).join(', '),
      learning_objectives: (activity.learning_objectives || []).join(', '),
      location: activity.location || 'indoor',
      energy_level: activity.energy_level || 'medium',
      instructions: activity.instructions || ''
    });
    setShowActivityDialog(true);
  };

  // Custom Plan Functions
  const initializeCustomPlan = () => {
    const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const defaultTimeSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '2:00 PM', '3:00 PM'];
    
    setCustomPlanForm({
      title: '',
      classroom_id: '',
      age_group: '',
      week_start: new Date().toISOString().split('T')[0],
      theme: '',
      days: weekDays.map(day => ({
        day,
        activities: defaultTimeSlots.map(time_slot => ({
          time_slot,
          activity_name: '',
          domain: '',
          duration_minutes: 30,
          materials: '',
          instructions: ''
        }))
      }))
    });
    setEditingPlan(null);
    setShowCustomPlanDialog(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setCustomPlanForm({
      title: plan.title || '',
      classroom_id: plan.classroom_id || '',
      age_group: plan.age_group || '',
      week_start: plan.week_start || new Date().toISOString().split('T')[0],
      theme: plan.theme || '',
      days: plan.days || []
    });
    setShowPreviewDialog(false);
    setShowCustomPlanDialog(true);
  };

  const handleSaveCustomPlan = async () => {
    if (!customPlanForm.classroom_id || !customPlanForm.age_group || !customPlanForm.week_start) {
      toast.error('Please fill in classroom, age group, and week start date');
      return;
    }

    // Calculate week_end
    const weekStart = new Date(customPlanForm.week_start);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 4);

    const classroom = classrooms.find(c => c.id === customPlanForm.classroom_id);
    
    const planData = {
      ...customPlanForm,
      title: customPlanForm.title || `${classroom?.name || 'Custom'} - Week of ${customPlanForm.week_start}`,
      classroom_name: classroom?.name || '',
      week_end: weekEnd.toISOString().split('T')[0],
      status: 'draft'
    };

    // Filter out empty activities
    planData.days = planData.days.map(day => ({
      ...day,
      activities: day.activities.filter(a => a.activity_name && a.activity_name.trim() !== '')
    }));

    try {
      setLoading(true);
      if (editingPlan?.id) {
        await axios.put(`${API}/learning/lesson-plans/${editingPlan.id}`, planData, getAuthHeaders());
        toast.success('Lesson plan updated!');
      } else {
        await axios.post(`${API}/learning/lesson-plans`, planData, getAuthHeaders());
        toast.success('Custom lesson plan saved!');
      }
      setShowCustomPlanDialog(false);
      setEditingPlan(null);
      fetchLessonPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    } finally {
      setLoading(false);
    }
  };

  const updatePlanActivity = (dayIndex, activityIndex, field, value) => {
    setCustomPlanForm(prev => {
      const newDays = [...prev.days];
      newDays[dayIndex].activities[activityIndex] = {
        ...newDays[dayIndex].activities[activityIndex],
        [field]: value
      };
      return { ...prev, days: newDays };
    });
  };

  const addActivityToDay = (dayIndex) => {
    setCustomPlanForm(prev => {
      const newDays = [...prev.days];
      newDays[dayIndex].activities.push({
        time_slot: '',
        activity_name: '',
        domain: '',
        duration_minutes: 30,
        materials: '',
        instructions: ''
      });
      return { ...prev, days: newDays };
    });
  };

  const removeActivityFromDay = (dayIndex, activityIndex) => {
    setCustomPlanForm(prev => {
      const newDays = [...prev.days];
      newDays[dayIndex].activities.splice(activityIndex, 1);
      return { ...prev, days: newDays };
    });
  };

  const openEditActivityInPlan = (dayIdx, actIdx, activity) => {
    setEditActivityTarget({ dayIndex: dayIdx, activityIndex: actIdx });
    setEditActivityForm({
      activity_name: activity.activity_name || '',
      domain: activity.domain || '',
      time_slot: activity.time_slot || '',
      duration_minutes: activity.duration_minutes || 30,
      materials: activity.materials || '',
      instructions: activity.instructions || ''
    });
    setShowEditActivityDialog(true);
  };

  const handleSaveActivityEdit = async () => {
    if (!editActivityTarget || !previewPlan?.id) return;

    const { dayIndex, activityIndex } = editActivityTarget;

    try {
      setLoading(true);
      await axios.put(
        `${API}/learning/lesson-plans/${previewPlan.id}/activities/${dayIndex}/${activityIndex}`,
        editActivityForm,
        getAuthHeaders()
      );
      
      // Update local state
      const updatedPlan = { ...previewPlan };
      updatedPlan.days[dayIndex].activities[activityIndex] = {
        ...updatedPlan.days[dayIndex].activities[activityIndex],
        ...editActivityForm
      };
      setPreviewPlan(updatedPlan);
      
      setShowEditActivityDialog(false);
      setEditActivityTarget(null);
      toast.success('Activity updated!');
    } catch (error) {
      toast.error('Failed to update activity');
    } finally {
      setLoading(false);
    }
  };

  const openPlanView = async (plan) => {
    setPreviewPlan(plan);
    setIsPlanSaved(true);
    setShowPreviewDialog(true);
    if (plan.id) {
      fetchPlanProgress(plan.id);
    }
  };

  const seedActivities = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/learning/activities/seed`, {}, getAuthHeaders());
      toast.success(res.data.message);
      fetchActivities();
    } catch (error) {
      toast.error('Failed to seed activities');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Learning & Lesson Plans">
      <div className="space-y-6" data-testid="learning-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BookOpen className="w-7 h-7 text-primary" />
              Learning Center
            </h1>
            <p className="text-muted-foreground">Create and manage lesson plans for your classrooms</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline"
              onClick={initializeCustomPlan}
              data-testid="custom-plan-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Custom Plan
            </Button>
            <Button 
              className="bg-primary hover:bg-primary/90"
              onClick={() => setShowGenerateDialog(true)}
              data-testid="generate-plan-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Lesson Plan
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="plans" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Lesson Plans
            </TabsTrigger>
            <TabsTrigger value="activities" className="flex items-center gap-2">
              <Library className="w-4 h-4" />
              Activities
            </TabsTrigger>
            <TabsTrigger value="observations" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Progress Report
            </TabsTrigger>
          </TabsList>

          {/* Lesson Plans Tab */}
          <TabsContent value="plans" className="mt-6">
            {lessonPlans.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Lesson Plans Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Generate your first lesson plan with one click!
                  </p>
                  <Button onClick={() => setShowGenerateDialog(true)}>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Plan
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {lessonPlans.map((plan) => (
                  <Card key={plan.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{plan.title}</CardTitle>
                          <CardDescription>{plan.classroom_name}</CardDescription>
                        </div>
                        <Badge variant={plan.status === 'approved' ? 'default' : 'secondary'}>
                          {plan.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          Week of {new Date(plan.week_start).toLocaleDateString()}
                        </div>
                        {plan.theme && (
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-primary" />
                            Theme: {plan.theme}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {AGE_GROUPS.find(a => a.value === plan.age_group)?.label || plan.age_group}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => openPlanView(plan)}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditPlan(plan)}
                          title="Edit plan"
                        >
                          <Edit2 className="w-4 h-4 text-primary" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeletePlan(plan.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Activity Library Tab */}
          <TabsContent value="activities" className="mt-6">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search activities..."
                    value={activitySearch}
                    onChange={(e) => setActivitySearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchActivities()}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select
                value={activityFilters.age_group || undefined}
                onValueChange={(v) => setActivityFilters({...activityFilters, age_group: v === 'all' ? '' : v})}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Ages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ages</SelectItem>
                  {AGE_GROUPS.map(ag => (
                    <SelectItem key={ag.value} value={ag.value}>{ag.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={activityFilters.domain || undefined}
                onValueChange={(v) => setActivityFilters({...activityFilters, domain: v === 'all' ? '' : v})}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Domains" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Domains</SelectItem>
                  {Object.entries(DOMAIN_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => { resetActivityForm(); setShowActivityDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Activity
              </Button>
              <Button variant="ghost" onClick={seedActivities} title="Seed default activities">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {/* Activities Grid */}
            {activities.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Library className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Activities Found</h3>
                  <p className="text-muted-foreground mb-4">
                    Seed the default activity library or create your own
                  </p>
                  <Button onClick={seedActivities}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Seed Default Activities
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {activities.map((activity) => (
                  <Card key={activity.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base">{activity.name}</CardTitle>
                        {activity.is_custom && (
                          <Badge variant="outline" className="text-xs">Custom</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {activity.description}
                      </p>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {(activity.domains || []).slice(0, 3).map(domain => (
                          <Badge key={domain} className={`text-xs ${DOMAIN_COLORS[domain] || 'bg-gray-100'}`}>
                            {DOMAIN_LABELS[domain] || domain}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {activity.duration_minutes}min
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {activity.energy_level}
                        </Badge>
                      </div>
                      {activity.is_custom && (
                        <div className="flex gap-2 mt-3 pt-3 border-t">
                          <Button variant="ghost" size="sm" onClick={() => openEditActivity(activity)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteActivity(activity.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Progress Report Tab */}
          <TabsContent value="observations" className="mt-6">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Select
                value={historyFilters.classroom_id || undefined}
                onValueChange={(v) => setHistoryFilters({...historyFilters, classroom_id: v === 'all' ? '' : v})}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All Classrooms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classrooms</SelectItem>
                  {classrooms.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={historyFilters.domain || undefined}
                onValueChange={(v) => setHistoryFilters({...historyFilters, domain: v === 'all' ? '' : v})}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Domains" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Domains</SelectItem>
                  {Object.entries(DOMAIN_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {historyFilters.classroom_id && (
                <Button 
                  variant="outline" 
                  onClick={handleExportProgressPDF}
                  disabled={loading}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              )}
            </div>

            {!observationHistory ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Loading Progress Data...</h3>
                </CardContent>
              </Card>
            ) : observationHistory.total_plans_analyzed === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Data Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create and complete lesson plans to see progress reports
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Child Progress Summary (if classroom selected) */}
                {childProgress && (
                  <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-primary" />
                        Classroom Progress Report: {childProgress.classroom_name}
                      </CardTitle>
                      <CardDescription>
                        {childProgress.weeks_analyzed} weeks analyzed 
                        {childProgress.date_range?.start && ` (${new Date(childProgress.date_range.start).toLocaleDateString()} - ${new Date(childProgress.date_range.end).toLocaleDateString()})`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {/* Highlights */}
                      {childProgress.highlights?.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            Highlights & Achievements
                          </h4>
                          <div className="space-y-2">
                            {childProgress.highlights.slice(0, 5).map((highlight, idx) => (
                              <div key={idx} className="bg-white/50 rounded-lg p-3 border border-green-100">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={`text-xs ${DOMAIN_COLORS[highlight.domain] || 'bg-gray-100'}`}>
                                    {DOMAIN_LABELS[highlight.domain] || highlight.domain}
                                  </Badge>
                                  <span className="text-sm font-medium">{highlight.activity}</span>
                                </div>
                                <p className="text-sm text-muted-foreground">{highlight.observation}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Areas of Focus */}
                      {childProgress.areas_of_focus?.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                            Areas to Focus On
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {childProgress.areas_of_focus.map((area, idx) => (
                              <Badge key={idx} variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                                {DOMAIN_LABELS[area.domain] || area.domain}: {area.reason}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Domain Coverage */}
                      <div>
                        <h4 className="font-semibold text-sm mb-3">Domain Coverage</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                          {Object.entries(childProgress.domain_coverage || {}).map(([domain, data]) => (
                            <div key={domain} className="bg-white/50 rounded-lg p-3 border">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium">{DOMAIN_LABELS[domain] || domain}</span>
                                <span className="text-xs text-primary font-bold">{data.completion_rate}%</span>
                              </div>
                              <Progress value={data.completion_rate} className="h-1.5" />
                              <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                                <span>{data.activities_completed}/{data.activities_planned}</span>
                                <span>{data.observations_count} notes</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Domain Insights */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      Domain Performance
                    </CardTitle>
                    <CardDescription>
                      Completion rates across all {observationHistory.total_plans_analyzed} lesson plans
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {observationHistory.domain_insights?.map((insight) => (
                        <div key={insight.domain} className="flex items-center gap-4">
                          <div className="w-32 text-sm font-medium">{DOMAIN_LABELS[insight.domain] || insight.domain}</div>
                          <div className="flex-1">
                            <Progress value={insight.completion_rate} className="h-2" />
                          </div>
                          <div className="w-20 text-right text-sm">
                            <span className="font-semibold">{insight.completion_rate}%</span>
                          </div>
                          <div className="w-24 text-right text-xs text-muted-foreground">
                            {insight.observations_count} notes
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Weekly Summaries */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-primary" />
                      Weekly Summaries
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {observationHistory.weekly_summaries?.map((week) => (
                        <div key={week.plan_id} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30 hover:bg-muted/50">
                          <div className="flex-1">
                            <p className="font-medium">{week.plan_title}</p>
                            <p className="text-sm text-muted-foreground">
                              {week.classroom_name} • Week of {new Date(week.week_start).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-primary">{week.completion_percentage}%</p>
                            <p className="text-xs text-muted-foreground">
                              {week.completed_activities}/{week.total_activities} activities • {week.observations_count} notes
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Observations */}
                {observationHistory.observations?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-primary" />
                        Recent Observations ({observationHistory.total_observations})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {observationHistory.observations.slice(0, 10).map((obs, idx) => (
                          <div key={idx} className="border-l-2 border-primary/30 pl-4 py-2">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={`text-xs ${DOMAIN_COLORS[obs.domain] || 'bg-gray-100'}`}>
                                {DOMAIN_LABELS[obs.domain] || obs.domain}
                              </Badge>
                              <span className="font-medium text-sm">{obs.activity_name}</span>
                              <span className="text-xs text-muted-foreground">• {obs.day}</span>
                            </div>
                            {obs.observation && (
                              <p className="text-sm text-muted-foreground mb-1">
                                <strong>Observation:</strong> {obs.observation}
                              </p>
                            )}
                            {obs.child_responses && (
                              <p className="text-sm text-muted-foreground mb-1">
                                <strong>Child Responses:</strong> {obs.child_responses}
                              </p>
                            )}
                            {obs.notes && (
                              <p className="text-sm text-muted-foreground">
                                <strong>Notes:</strong> {obs.notes}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {obs.classroom_name} • Week of {new Date(obs.week_start).toLocaleDateString()}
                              {obs.completed_by && ` • by ${obs.completed_by}`}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Generate Plan Dialog */}
        <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Generate Lesson Plan
              </DialogTitle>
              <DialogDescription>
                {useAiGeneration 
                  ? "AI-powered lesson plan based on Wisconsin WMELS framework"
                  : "Auto-generate from your activity library"
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* AI Generation Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <Label htmlFor="ai-toggle" className="cursor-pointer font-medium">Use AI Generation</Label>
                </div>
                <Checkbox
                  id="ai-toggle"
                  checked={useAiGeneration}
                  onCheckedChange={(v) => setUseAiGeneration(v)}
                  data-testid="ai-toggle"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Classroom *</Label>
                <Select
                  value={generateForm.classroom_id}
                  onValueChange={(v) => setGenerateForm({...generateForm, classroom_id: v})}
                >
                  <SelectTrigger data-testid="classroom-select">
                    <SelectValue placeholder="Select classroom" />
                  </SelectTrigger>
                  <SelectContent>
                    {classrooms.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Age Group *</Label>
                <Select
                  value={generateForm.age_group}
                  onValueChange={(v) => setGenerateForm({...generateForm, age_group: v})}
                >
                  <SelectTrigger data-testid="age-group-select">
                    <SelectValue placeholder="Select age group" />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_GROUPS.map(ag => (
                      <SelectItem key={ag.value} value={ag.value}>{ag.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Week Starting *</Label>
                <Input
                  type="date"
                  value={generateForm.week_start}
                  onChange={(e) => setGenerateForm({...generateForm, week_start: e.target.value})}
                  data-testid="week-start-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Theme (Optional)</Label>
                <Input
                  placeholder="e.g., Spring Growth, Ocean Life"
                  value={generateForm.theme}
                  onChange={(e) => setGenerateForm({...generateForm, theme: e.target.value})}
                />
                <div className="flex flex-wrap gap-1 mt-1">
                  {themes.slice(0, 3).flatMap(t => t.suggestions.slice(0, 2)).map(theme => (
                    <Badge
                      key={theme}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary/10"
                      onClick={() => setGenerateForm({...generateForm, theme})}
                    >
                      {theme}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Special Focus (AI only) */}
              {useAiGeneration && (
                <div className="space-y-2">
                  <Label>Special Focus (Optional)</Label>
                  <Input
                    placeholder="e.g., letter recognition, preparing for spring"
                    value={generateForm.special_focus}
                    onChange={(e) => setGenerateForm({...generateForm, special_focus: e.target.value})}
                    data-testid="special-focus-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Guide the AI to emphasize specific skills or topics
                  </p>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="outdoor"
                  checked={generateForm.include_outdoor}
                  onCheckedChange={(v) => setGenerateForm({...generateForm, include_outdoor: v})}
                />
                <Label htmlFor="outdoor" className="cursor-pointer">Include outdoor activities</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleGeneratePlan} disabled={loading} data-testid="generate-submit-btn">
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {useAiGeneration ? 'Generate with AI' : 'Generate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Plan Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={(open) => {
          setShowPreviewDialog(open);
          if (!open) {
            setPlanProgress(null);
            setIsPlanSaved(false);
            setPreviewPlan(null);
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                {previewPlan?.title || 'Lesson Plan Preview'}
                {previewPlan?.generated_by_ai && (
                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs">
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI Generated
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                {isPlanSaved ? 'Track activity completion and add observations' : 'Review and save your lesson plan'}
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[500px] pr-4">
              {previewPlan && (
                <div className="space-y-6">
                  {/* Plan Info */}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">{previewPlan.classroom_name}</Badge>
                    <Badge variant="outline">{AGE_GROUPS.find(a => a.value === previewPlan.age_group)?.label}</Badge>
                    {previewPlan.theme && <Badge className="bg-primary/10 text-primary">{previewPlan.theme}</Badge>}
                    <Badge variant="outline">
                      {new Date(previewPlan.week_start).toLocaleDateString()} - {new Date(previewPlan.week_end).toLocaleDateString()}
                    </Badge>
                  </div>
                  
                  {/* Learning Goals (for AI plans) */}
                  {previewPlan.learning_goals && previewPlan.learning_goals.length > 0 && (
                    <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
                      <CardContent className="py-4">
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <Star className="w-4 h-4 text-primary" />
                          Learning Goals for This Week
                        </h4>
                        <ul className="space-y-1">
                          {previewPlan.learning_goals.map((goal, idx) => (
                            <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                              {goal}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* Progress Bar (only for saved plans) */}
                  {planProgress && (
                    <Card className="bg-muted/30">
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-primary" />
                            Weekly Progress
                          </span>
                          <span className="text-sm font-semibold text-primary">
                            {planProgress.completed_activities}/{planProgress.total_activities} activities ({planProgress.completion_percentage}%)
                          </span>
                        </div>
                        <Progress value={planProgress.completion_percentage} className="h-2" />
                        
                        {/* Domain Progress */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {Object.entries(planProgress.domain_progress || {}).map(([domain, progress]) => (
                            <Badge 
                              key={domain} 
                              variant="outline" 
                              className={`text-xs ${progress.completed === progress.total ? 'bg-green-100 text-green-800' : ''}`}
                            >
                              {DOMAIN_LABELS[domain] || domain}: {progress.completed}/{progress.total}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Days */}
                  {previewPlan.days?.map((day, dayIdx) => (
                    <Card key={dayIdx}>
                      <CardHeader className="py-3 bg-muted/50">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">{day.day}</CardTitle>
                          {isPlanSaved && (
                            <span className="text-xs text-muted-foreground">
                              {day.activities?.filter(a => a.completed).length || 0}/{day.activities?.length || 0} done
                            </span>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="divide-y">
                          {day.activities?.map((activity, actIdx) => (
                            <div 
                              key={actIdx} 
                              className={`flex items-center gap-4 p-4 hover:bg-muted/30 ${activity.completed ? 'bg-green-50/50' : ''}`}
                            >
                              {/* Completion checkbox (only for saved plans) */}
                              {isPlanSaved && (
                                <button
                                  onClick={() => handleToggleComplete(dayIdx, actIdx, activity.completed)}
                                  className="flex-shrink-0"
                                >
                                  {activity.completed ? (
                                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                                  ) : (
                                    <Circle className="w-6 h-6 text-muted-foreground hover:text-primary" />
                                  )}
                                </button>
                              )}
                              
                              <div className="flex items-center gap-2 text-sm text-muted-foreground w-20">
                                <Clock className="w-4 h-4" />
                                {activity.time_slot}
                              </div>
                              
                              <div className="flex-1">
                                <p className={`font-medium ${activity.completed ? 'text-muted-foreground line-through' : ''}`}>
                                  {activity.activity_name}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge className={`text-xs ${DOMAIN_COLORS[activity.domain] || 'bg-gray-100'}`}>
                                    {DOMAIN_LABELS[activity.domain] || activity.domain}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">{activity.duration_minutes} min</span>
                                  {activity.observation && (
                                    <Badge variant="outline" className="text-xs bg-blue-50">
                                      <MessageSquare className="w-3 h-3 mr-1" />
                                      Has notes
                                    </Badge>
                                  )}
                                </div>
                                {activity.completed_by_name && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Completed by {activity.completed_by_name}
                                  </p>
                                )}
                              </div>
                              
                              {/* Edit and Observation buttons (only for saved plans) */}
                              {isPlanSaved && (
                                <div className="flex gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditActivityInPlan(dayIdx, actIdx, activity)}
                                    title="Edit activity"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openObservationDialog(dayIdx, actIdx, activity)}
                                    title="Add observation"
                                  >
                                    <ClipboardList className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </ScrollArea>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setShowPreviewDialog(false); setPreviewPlan(null); setPlanProgress(null); }}>
                Close
              </Button>
              {isPlanSaved && (
                <>
                  <Button variant="outline" onClick={() => openEditPlan(previewPlan)}>
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Plan
                  </Button>
                  <Button variant="outline" onClick={handleExportLessonPlanPDF} disabled={loading} data-testid="export-pdf-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                </>
              )}
              {!isPlanSaved && (
                <Button variant="outline" onClick={handleRegeneratePlan}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
              )}
              {!isPlanSaved && (
                <Button onClick={handleSavePlan} disabled={loading} data-testid="save-plan-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Save Plan
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Observation Dialog */}
        <Dialog open={showObservationDialog} onOpenChange={setShowObservationDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-primary" />
                Activity Observation
              </DialogTitle>
              <DialogDescription>
                {observationTarget?.activity?.activity_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Teacher Notes</Label>
                <Textarea
                  placeholder="Any notes about the activity (setup, adjustments made, etc.)"
                  value={observationForm.notes}
                  onChange={(e) => setObservationForm({...observationForm, notes: e.target.value})}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Child Responses</Label>
                <Textarea
                  placeholder="How did the children respond? What did they enjoy or struggle with?"
                  value={observationForm.child_responses}
                  onChange={(e) => setObservationForm({...observationForm, child_responses: e.target.value})}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Learning Observations</Label>
                <Textarea
                  placeholder="What learning did you observe? Any milestones or skills demonstrated?"
                  value={observationForm.observation}
                  onChange={(e) => setObservationForm({...observationForm, observation: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowObservationDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveObservation} disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                Save Observation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Activity Dialog */}
        <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
          <DialogContent className="max-w-lg max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>
                {editingActivity ? 'Edit Activity' : 'Add New Activity'}
              </DialogTitle>
            </DialogHeader>
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Activity Name *</Label>
                  <Input
                    value={activityForm.name}
                    onChange={(e) => setActivityForm({...activityForm, name: e.target.value})}
                    placeholder="e.g., Color Sorting"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={activityForm.description}
                    onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                    placeholder="Brief description of the activity"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Age Groups *</Label>
                  <div className="flex flex-wrap gap-2">
                    {AGE_GROUPS.map(ag => (
                      <Badge
                        key={ag.value}
                        variant={activityForm.age_groups.includes(ag.value) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          const newAges = activityForm.age_groups.includes(ag.value)
                            ? activityForm.age_groups.filter(a => a !== ag.value)
                            : [...activityForm.age_groups, ag.value];
                          setActivityForm({...activityForm, age_groups: newAges});
                        }}
                      >
                        {ag.label.split(' ')[0]}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Learning Domains *</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(DOMAIN_LABELS).map(([key, label]) => (
                      <Badge
                        key={key}
                        variant={activityForm.domains.includes(key) ? 'default' : 'outline'}
                        className={`cursor-pointer ${activityForm.domains.includes(key) ? DOMAIN_COLORS[key] : ''}`}
                        onClick={() => {
                          const newDomains = activityForm.domains.includes(key)
                            ? activityForm.domains.filter(d => d !== key)
                            : [...activityForm.domains, key];
                          setActivityForm({...activityForm, domains: newDomains});
                        }}
                      >
                        {label}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Duration (minutes)</Label>
                    <Input
                      type="number"
                      min={5}
                      max={60}
                      value={activityForm.duration_minutes}
                      onChange={(e) => setActivityForm({...activityForm, duration_minutes: parseInt(e.target.value) || 15})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Energy Level</Label>
                    <Select
                      value={activityForm.energy_level}
                      onValueChange={(v) => setActivityForm({...activityForm, energy_level: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select
                    value={activityForm.location}
                    onValueChange={(v) => setActivityForm({...activityForm, location: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="indoor">Indoor</SelectItem>
                      <SelectItem value="outdoor">Outdoor</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Materials (comma-separated)</Label>
                  <Input
                    value={activityForm.materials}
                    onChange={(e) => setActivityForm({...activityForm, materials: e.target.value})}
                    placeholder="e.g., Crayons, Paper, Scissors"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Learning Objectives (comma-separated)</Label>
                  <Input
                    value={activityForm.learning_objectives}
                    onChange={(e) => setActivityForm({...activityForm, learning_objectives: e.target.value})}
                    placeholder="e.g., Fine motor control, Color recognition"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Instructions (optional)</Label>
                  <Textarea
                    value={activityForm.instructions}
                    onChange={(e) => setActivityForm({...activityForm, instructions: e.target.value})}
                    placeholder="Step-by-step instructions..."
                    rows={3}
                  />
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowActivityDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveActivity} disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                {editingActivity ? 'Update' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Custom Plan Dialog */}
        <Dialog open={showCustomPlanDialog} onOpenChange={setShowCustomPlanDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary" />
                {editingPlan ? 'Edit Lesson Plan' : 'Create Custom Lesson Plan'}
              </DialogTitle>
              <DialogDescription>
                {editingPlan ? 'Modify your lesson plan activities' : 'Build your own weekly lesson plan from scratch'}
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-6">
                {/* Plan Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="space-y-2">
                    <Label>Plan Title</Label>
                    <Input
                      placeholder="e.g., Spring Week 1"
                      value={customPlanForm.title}
                      onChange={(e) => setCustomPlanForm({...customPlanForm, title: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Classroom *</Label>
                    <Select
                      value={customPlanForm.classroom_id}
                      onValueChange={(v) => setCustomPlanForm({...customPlanForm, classroom_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select classroom" />
                      </SelectTrigger>
                      <SelectContent>
                        {classrooms.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Age Group *</Label>
                    <Select
                      value={customPlanForm.age_group}
                      onValueChange={(v) => setCustomPlanForm({...customPlanForm, age_group: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select age group" />
                      </SelectTrigger>
                      <SelectContent>
                        {AGE_GROUPS.map(ag => (
                          <SelectItem key={ag.value} value={ag.value}>{ag.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Week Starting *</Label>
                    <Input
                      type="date"
                      value={customPlanForm.week_start}
                      onChange={(e) => setCustomPlanForm({...customPlanForm, week_start: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Theme (Optional)</Label>
                    <Input
                      placeholder="e.g., Ocean Life, Spring Growth"
                      value={customPlanForm.theme}
                      onChange={(e) => setCustomPlanForm({...customPlanForm, theme: e.target.value})}
                    />
                  </div>
                </div>

                {/* Days and Activities */}
                <div className="space-y-4">
                  {customPlanForm.days.map((day, dayIdx) => (
                    <Card key={dayIdx}>
                      <CardHeader className="py-3 bg-muted/50">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">{day.day}</CardTitle>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => addActivityToDay(dayIdx)}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Activity
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {day.activities.map((activity, actIdx) => (
                            <div key={actIdx} className="grid grid-cols-12 gap-2 items-start p-3 border rounded-lg bg-background">
                              <div className="col-span-2">
                                <Label className="text-xs text-muted-foreground">Time</Label>
                                <Input
                                  placeholder="9:00 AM"
                                  value={activity.time_slot}
                                  onChange={(e) => updatePlanActivity(dayIdx, actIdx, 'time_slot', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="col-span-4">
                                <Label className="text-xs text-muted-foreground">Activity Name</Label>
                                <Input
                                  placeholder="Activity name..."
                                  value={activity.activity_name}
                                  onChange={(e) => updatePlanActivity(dayIdx, actIdx, 'activity_name', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="col-span-2">
                                <Label className="text-xs text-muted-foreground">Domain</Label>
                                <Select
                                  value={activity.domain || undefined}
                                  onValueChange={(v) => updatePlanActivity(dayIdx, actIdx, 'domain', v)}
                                >
                                  <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Object.entries(DOMAIN_LABELS).map(([key, label]) => (
                                      <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-1">
                                <Label className="text-xs text-muted-foreground">Min</Label>
                                <Input
                                  type="number"
                                  placeholder="30"
                                  value={activity.duration_minutes}
                                  onChange={(e) => updatePlanActivity(dayIdx, actIdx, 'duration_minutes', parseInt(e.target.value) || 30)}
                                  className="h-9"
                                />
                              </div>
                              <div className="col-span-2">
                                <Label className="text-xs text-muted-foreground">Materials</Label>
                                <Input
                                  placeholder="Materials..."
                                  value={activity.materials}
                                  onChange={(e) => updatePlanActivity(dayIdx, actIdx, 'materials', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="col-span-1 flex items-end justify-center pb-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeActivityFromDay(dayIdx, actIdx)}
                                  className="h-9 w-9 p-0"
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          {day.activities.length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              No activities yet. Click "Add Activity" to start.
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCustomPlanDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveCustomPlan} disabled={loading}>
                {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingPlan ? 'Update Plan' : 'Save Plan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Activity in Plan Dialog */}
        <Dialog open={showEditActivityDialog} onOpenChange={setShowEditActivityDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-primary" />
                Edit Activity
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Activity Name</Label>
                <Input
                  value={editActivityForm.activity_name}
                  onChange={(e) => setEditActivityForm({...editActivityForm, activity_name: e.target.value})}
                  placeholder="Activity name..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Time Slot</Label>
                  <Input
                    value={editActivityForm.time_slot}
                    onChange={(e) => setEditActivityForm({...editActivityForm, time_slot: e.target.value})}
                    placeholder="9:00 AM"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duration (min)</Label>
                  <Input
                    type="number"
                    value={editActivityForm.duration_minutes}
                    onChange={(e) => setEditActivityForm({...editActivityForm, duration_minutes: parseInt(e.target.value) || 30})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Domain</Label>
                <Select
                  value={editActivityForm.domain || undefined}
                  onValueChange={(v) => setEditActivityForm({...editActivityForm, domain: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select domain" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(DOMAIN_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Materials</Label>
                <Input
                  value={editActivityForm.materials}
                  onChange={(e) => setEditActivityForm({...editActivityForm, materials: e.target.value})}
                  placeholder="Materials needed..."
                />
              </div>
              <div className="space-y-2">
                <Label>Instructions</Label>
                <Textarea
                  value={editActivityForm.instructions}
                  onChange={(e) => setEditActivityForm({...editActivityForm, instructions: e.target.value})}
                  placeholder="Activity instructions..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditActivityDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveActivityEdit} disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LearningPage;
